import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../api";
import { useSettings } from "./useSettings";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";

export default function SettingsServices() {
  const { s, setS, save } = useSettings();
  const [snowWarn, setSnowWarn] = useState(null);
  const [snowPhotoAck, setSnowPhotoAck] = useState(false);
  const nav = useNavigate();
  if (!s) return <p className="text-muted-foreground">Loading…</p>;

  const saveSnowPhotoMin = () => {
    const v = Math.max(0, Math.min(5, Number(s.snow_photo_min ?? 0)));
    if (v === 0) return setSnowPhotoAck(true);  // zero photo minimum needs the evidence warning acknowledged
    save({ snow_photo_min: v });
  };

  const toggleSnow = async () => {
    if (!(s.offers_snow ?? true)) return save({ offers_snow: true });  // turning ON — never warns
    try {
      const r = await api.get("/agreements/active-snow");
      if (r.data.count > 0) { setSnowWarn(r.data); return; }
    } catch { /* count unavailable — fall through to plain save */ }
    save({ offers_snow: false });
  };

  return (
    <div className="space-y-8">
      <section id="set-lines" className="card-flat space-y-3 p-6" data-testid="business-lines-settings">
        <p className="label-caps">What do you offer?</p>
        <p className="text-sm text-muted-foreground">Not every business runs both lines. Turn one off to hide its pricing, weather watch, and scheduling features — turn it back on anytime.</p>
        <label className="flex items-center justify-between rounded-md border border-border p-4">
          <span className="text-sm font-semibold">Lawn &amp; property maintenance (spring–fall)</span>
          <button type="button" data-testid="offers-lawn-toggle" onClick={() => save({ offers_lawn: !(s.offers_lawn ?? true) })}
            className={`h-8 w-14 shrink-0 rounded-full transition-colors ${(s.offers_lawn ?? true) ? "bg-primary" : "bg-muted"}`}>
            <span className={`block h-6 w-6 translate-x-1 rounded-full bg-white transition-transform ${(s.offers_lawn ?? true) ? "translate-x-7" : ""}`} />
          </button>
        </label>
        <label className="flex items-center justify-between rounded-md border border-border p-4">
          <span className="text-sm font-semibold">Snow removal (winter)</span>
          <button type="button" data-testid="offers-snow-toggle" onClick={toggleSnow}
            className={`h-8 w-14 shrink-0 rounded-full transition-colors ${(s.offers_snow ?? true) ? "bg-primary" : "bg-muted"}`}>
            <span className={`block h-6 w-6 translate-x-1 rounded-full bg-white transition-transform ${(s.offers_snow ?? true) ? "translate-x-7" : ""}`} />
          </button>
        </label>
      </section>

      {snowPhotoAck && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="snow-photo-ack-dialog">
          <div className="card-flat w-full max-w-md space-y-4 bg-background p-6">
            <p className="text-base font-bold">Save with no snow photo minimum?</p>
            <p className="text-sm" data-testid="snow-photo-ack-text">Without photos, your visit records rely on GPS and map snapshots only. That is weaker evidence in a slip-and-fall claim and may affect your insurance defence or premium. Check with your broker.</p>
            <button type="button" data-testid="snow-photo-ack-save" onClick={() => { setSnowPhotoAck(false); save({ snow_photo_min: 0, snow_photo_ack: true }); }}
              className="h-14 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground">I understand — save</button>
            <button type="button" data-testid="snow-photo-ack-cancel" onClick={() => setSnowPhotoAck(false)}
              className="h-14 w-full rounded-md border-2 border-border text-sm font-bold">Keep photos required</button>
          </div>
        </div>
      )}

      {snowWarn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="snow-off-warn-dialog">
          <div className="card-flat w-full max-w-md space-y-4 bg-background p-6">
            <p className="text-base font-bold">Turn snow removal off?</p>
            <p className="text-sm" data-testid="snow-off-warn-text">
              You have <strong>{snowWarn.count}</strong> active snow agreements. Turning snow removal off hides snow controls but does NOT stop their billing. Deactivate them in Agreements to stop invoicing.
            </p>
            <button type="button" data-testid="snow-off-warn-agreements-btn" onClick={() => { setSnowWarn(null); nav("/properties?snow=1"); }}
              className="h-14 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground">Go to Agreements</button>
            <button type="button" data-testid="snow-off-warn-anyway-btn" onClick={() => { setSnowWarn(null); save({ offers_snow: false }); }}
              className="h-14 w-full rounded-md border-2 border-border text-sm font-bold">Turn off anyway</button>
          </div>
        </div>
      )}

      {s.offers_snow !== false && (
      <section id="set-snow-rates" className="card-flat space-y-3 p-6" data-testid="snow-settings">
        <p className="label-caps">Snow removal pricing</p>
        <p className="text-sm text-muted-foreground">Per-visit rate by driveway size. Snow services set to "Size-based rate" on an agreement auto-fill from here; each property's size is set on its page.</p>
        {[["small", "Small (1–2 car)"], ["medium", "Medium (3–4 car)"], ["large", "Large (long/wide)"], ["estate", "Estate / rural"]].map(([k, label]) => (
          <div key={k} className="flex items-center gap-2">
            <span className="flex-1 text-sm font-semibold">{label}</span>
            <span className="font-bold">$</span>
            <input data-testid={`snow-rate-${k}`} type="number" className="field-input w-24" value={s.snow_size_rates?.[k] ?? ""} onFocus={(e) => e.target.select()}
              onChange={(e) => setS({ ...s, snow_size_rates: { ...(s.snow_size_rates || {}), [k]: Number(e.target.value) } })} />
          </div>
        ))}
        <button data-testid="snow-rates-save" onClick={() => save({ snow_size_rates: s.snow_size_rates })} className="h-12 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground">Save size rates</button>
      </section>
      )}

      <section id="set-catalog" className="card-flat space-y-3 p-6" data-testid="addon-catalog-settings">
        <p className="label-caps">Upsell add-on catalog</p>
        <p className="text-sm text-muted-foreground">Walkway shovelling, salting, anything you upsell. Your crew ticks these at job completion and they land on the invoice automatically.</p>
        {(s.addon_catalog || []).map((a, i) => (
          <div key={a._k} className="flex items-center gap-2" data-testid={`catalog-row-${i}`}>
            <input data-testid={`catalog-name-${i}`} className="field-input flex-1" placeholder="Add-on name" value={a.name}
              onChange={(e) => setS({ ...s, addon_catalog: s.addon_catalog.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
            <span className="font-bold">$</span>
            <input data-testid={`catalog-price-${i}`} type="number" className="field-input w-24" value={a.price} onFocus={(e) => e.target.select()}
              onChange={(e) => setS({ ...s, addon_catalog: s.addon_catalog.map((x, j) => (j === i ? { ...x, price: Number(e.target.value) } : x)) })} />
            <button type="button" data-testid={`catalog-del-${i}`} onClick={() => setS({ ...s, addon_catalog: s.addon_catalog.filter((_, j) => j !== i) })}
              className="h-12 w-12 shrink-0 rounded-md border border-border text-destructive"><Trash size={18} className="mx-auto" /></button>
          </div>
        ))}
        <button type="button" data-testid="catalog-add" onClick={() => setS({ ...s, addon_catalog: [...(s.addon_catalog || []), { name: "", price: 0, _k: crypto.randomUUID() }] })}
          className="flex h-12 w-full items-center justify-center gap-2 rounded-md border border-dashed border-border text-sm font-bold text-muted-foreground">
          <Plus size={16} weight="bold" /> Add add-on
        </button>
        <button data-testid="catalog-save" onClick={() => save({ addon_catalog: (s.addon_catalog || []).filter((a) => a.name).map(({ _k, ...a }) => a) })} className="h-12 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground">Save catalog</button>
      </section>

      <section id="set-photos" className="card-flat space-y-3 p-6" data-testid="min-photos-settings">
        <p className="label-caps">Completion photos</p>
        <p className="text-sm text-muted-foreground">Minimum photos required before a job can be marked complete — applies to you and your crew (max 5).</p>
        <div className="flex items-center gap-2">
          <span className="w-24 text-sm font-semibold">Lawn jobs</span>
          <input data-testid="lawn-photo-min" type="number" min="0" max="5" className="field-input w-24" value={s.lawn_photo_min ?? s.min_completion_photos ?? 2} onFocus={(e) => e.target.select()}
            onChange={(e) => setS({ ...s, lawn_photo_min: Number(e.target.value) })} />
          <span className="text-sm font-semibold">photos</span>
          <button data-testid="lawn-photo-min-save" onClick={() => save({ lawn_photo_min: Math.max(0, Math.min(5, Number(s.lawn_photo_min ?? s.min_completion_photos ?? 2))) })} className="h-12 flex-1 rounded-md bg-primary text-sm font-bold text-primary-foreground">Save</button>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-24 text-sm font-semibold">Snow stops</span>
          <input data-testid="snow-photo-min" type="number" min="0" max="5" className="field-input w-24" value={s.snow_photo_min ?? 0} onFocus={(e) => e.target.select()}
            onChange={(e) => setS({ ...s, snow_photo_min: Number(e.target.value) })} />
          <span className="text-sm font-semibold">photos</span>
          <button data-testid="snow-photo-min-save" onClick={saveSnowPhotoMin} className="h-12 flex-1 rounded-md bg-primary text-sm font-bold text-primary-foreground">Save</button>
        </div>
        {Number(s.snow_photo_min ?? 0) === 0 && s.snow_photo_ack_at && (
          <p className="text-xs text-muted-foreground" data-testid="snow-photo-ack-line">No-photo snow stops acknowledged by {s.snow_photo_ack_by} on {String(s.snow_photo_ack_at).slice(0, 10)}.</p>
        )}
      </section>
    </div>
  );
}
